import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { DollarSign, Tag, Trash2, Plus, TrendingUp, Percent, Calendar, Building2, Save, Loader2, Package, Edit2, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import DynamicPromotions from './DynamicPromotions';
import { useBodyLock } from '../hooks/useBodyLock';
import { useAuth } from '../contexts/AuthContext';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const PricingManagement = () => {
  const { user } = useAuth();
  const [prices, setPrices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  const [localPlans, setLocalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // 🏢 B2B: Enterprise pricing state
  const [enterprisePricing, setEnterprisePricing] = useState(null);
  const [enterprisePrice, setEnterprisePrice] = useState('');
  const [enterpriseQuota, setEnterpriseQuota] = useState('150');  // 🔒 P1 MOA: Quota B2B (défaut: 150)
  const [savingEnterprisePrice, setSavingEnterprisePrice] = useState(false);
  
  // 🎯 Subscription packs (quotas de sessions)
  const [subscriptionPacks, setSubscriptionPacks] = useState([]);
  const [showEditQuotaModal, setShowEditQuotaModal] = useState(false);
  const [editingPack, setEditingPack] = useState(null);

  // ✨ CoachDIA Discovery pricing (B2C COACHDIA Prism) — source unique, pas de page séparée
  const [discoveryPricing, setDiscoveryPricing] = useState(null); // active_pricing
  const [discoveryForm, setDiscoveryForm] = useState({
    base_price_eur: 149,
    currency: 'EUR',
    promo_enabled: false,
    promo_price_eur: '',
    promo_label: '',
    promo_starts_at: '',
    promo_ends_at: '',
  });
  const [savingDiscovery, setSavingDiscovery] = useState(false);

  // 🆕 ITER 234: COACHDIA Pulse packs (5 packs éditables)
  const [teamPacks, setTeamPacks] = useState([]);
  const [savingTeamPackId, setSavingTeamPackId] = useState(null);
  const [expandedPack, setExpandedPack] = useState(null);

  // 🔧 FIX MOBILE: Body lock pour les modals
  useBodyLock(showCreateModal || showEditPriceModal || showEditQuotaModal);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [pricesRes, promotionsRes, statsRes, localPlansRes, enterpriseRes, packsRes, discoveryRes, teamPacksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/pricing/plans`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/pricing/promotions`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/pricing/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/pricing/local-plans`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/pricing/enterprise`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/subscription-packs/?include_inactive=true`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/discovery/pricing`, { headers }),
        fetch(`${BACKEND_URL}/api/discovery/admin/team-insights/packs`, { headers }),
      ]);

      if (pricesRes.ok) {
        const data = await pricesRes.json();
        setPrices(data.prices || []);
      }

      if (promotionsRes.ok) {
        const data = await promotionsRes.json();
        setPromotions(data.promotions || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (localPlansRes.ok) {
        const data = await localPlansRes.json();
        setLocalPlans(data.plans || []);
      }
      
      // 🏢 B2B: Fetch enterprise pricing
      if (enterpriseRes.ok) {
        const data = await enterpriseRes.json();
        setEnterprisePricing(data);
        setEnterprisePrice(data.enterprise_unit_price_monthly?.toString() || '');
        setEnterpriseQuota(data.enterprise_monthly_quota?.toString() || '150');  // 🔒 P1 MOA: Quota B2B
      } else if (enterpriseRes.status === 404) {
        // Config not yet set
        setEnterprisePricing(null);
        setEnterprisePrice('');
      }
      
      // 🎯 Subscription packs (quotas)
      if (packsRes.ok) {
        const data = await packsRes.json();
        setSubscriptionPacks(data.packs || []);
      }

      // ✨ Discovery (COACHDIA Prism B2C)
      if (discoveryRes.ok) {
        const data = await discoveryRes.json();
        const cfg = data.config || {};
        setDiscoveryPricing(data.active_pricing || null);
        setDiscoveryForm({
          base_price_eur: cfg.base_price_eur ?? 149,
          currency: cfg.currency || 'EUR',
          promo_enabled: !!cfg.promo_enabled,
          promo_price_eur: cfg.promo_price_eur ?? '',
          promo_label: cfg.promo_label || '',
          promo_starts_at: toLocalDatetime(cfg.promo_starts_at),
          promo_ends_at: toLocalDatetime(cfg.promo_ends_at),
        });
      }

      // 🧩 COACHDIA Pulse packs
      if (teamPacksRes.ok) {
        const data = await teamPacksRes.json();
        setTeamPacks(data.packs || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePromotion = async (couponId) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/promotion/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Promotion supprimée');
        fetchData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleUpdatePrice = async (planId, newPrice) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/local-plans/${planId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ price: parseFloat(newPrice) })
      });

      if (response.ok) {
        toast.success('Prix mis à jour');
        fetchData();
        setShowEditPriceModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de mise à jour');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // 🏢 B2B: Update enterprise pricing (prix + quota)
  const handleUpdateEnterprisePrice = async () => {
    const priceValue = parseFloat(enterprisePrice);
    const quotaValue = parseInt(enterpriseQuota);
    
    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error('Le prix doit être un nombre positif');
      return;
    }
    
    if (isNaN(quotaValue) || quotaValue < 1 || quotaValue > 1000) {
      toast.error('Le quota doit être entre 1 et 1000 sessions');
      return;
    }
    
    setSavingEnterprisePrice(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/enterprise`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          enterprise_unit_price_monthly: priceValue,
          enterprise_monthly_quota: quotaValue  // 🔒 P1 MOA: Quota B2B
        })
      });

      // Parse JSON une seule fois, avant de vérifier le statut
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('[B2B Pricing] Erreur parsing JSON:', parseError);
        toast.error('Erreur: réponse serveur invalide');
        return;
      }

      if (response.ok) {
        setEnterprisePricing(data);
        toast.success(`Configuration entreprise mise à jour: ${priceValue}€/licence/mois, ${quotaValue} sessions/mois`);
      } else {
        // Message d'erreur explicite depuis le backend
        const errorMsg = data?.detail || `Erreur serveur (HTTP ${response.status})`;
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[B2B Pricing] Erreur réseau:', error);
      // Message d'erreur explicite avec contexte
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Erreur réseau: impossible de contacter le serveur');
      } else {
        toast.error(`Erreur: ${error.message || 'problème de connexion au serveur'}`);
      }
    } finally {
      setSavingEnterprisePrice(false);
    }
  };

  // 🎯 Update subscription pack quota
  const handleUpdatePackQuota = async (packId, newQuota) => {
    try {
      const token = localStorage.getItem('token');
      const adminEmail = user?.email || 'admin@unknown.com';
      
      const response = await fetch(`${BACKEND_URL}/api/admin/subscription-packs/${packId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessions_quota: parseInt(newQuota),
          admin_email: adminEmail
        })
      });

      if (response.ok) {
        toast.success(`Quota mis à jour pour le pack ${packId}`);
        fetchData();
        setShowEditQuotaModal(false);
        setEditingPack(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de mise à jour');
      }
    } catch (error) {
      console.error('Error updating pack quota:', error);
      toast.error('Erreur de connexion');
    }
  };

  // ✨ Discovery helpers (ISO <-> datetime-local)
  const toLocalDatetime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (_) { return ''; }
  };

  const toServerIso = (local) => {
    if (!local) return null;
    try { return new Date(local).toISOString(); } catch (_) { return null; }
  };

  // ✨ Save Discovery pricing (COACHDIA Prism B2C)
  const handleSaveDiscoveryPricing = async () => {    const base = parseFloat(discoveryForm.base_price_eur);
    if (isNaN(base) || base <= 0) {
      toast.error('Le prix de base doit être un nombre positif');
      return;
    }
    if (discoveryForm.promo_enabled) {
      const promo = parseFloat(discoveryForm.promo_price_eur);
      // 🎁 Iter336 — Gratuité premium : promo == 0 désormais autorisée
      // (offre "accès offert"). On exige label + dates de début/fin pour traçabilité.
      if (isNaN(promo) || promo < 0 || promo > base) {
        toast.error('Le prix promo doit être ≥ 0 et inférieur ou égal au prix de base');
        return;
      }
      if (promo === 0) {
        if (!discoveryForm.promo_label || !discoveryForm.promo_label.trim()) {
          toast.error('Une promo gratuite (0 €) exige un libellé de campagne pour traçabilité');
          return;
        }
        if (!discoveryForm.promo_starts_at) {
          toast.error('Une promo gratuite (0 €) exige une date de début');
          return;
        }
        if (!discoveryForm.promo_ends_at) {
          toast.error('Une promo gratuite (0 €) exige une date de fin');
          return;
        }
      }
    }
    setSavingDiscovery(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        base_price_eur: base,
        currency: discoveryForm.currency,
        promo_enabled: discoveryForm.promo_enabled,
        promo_price_eur: discoveryForm.promo_price_eur ? parseFloat(discoveryForm.promo_price_eur) : null,
        promo_label: discoveryForm.promo_label || null,
        promo_starts_at: toServerIso(discoveryForm.promo_starts_at),
        promo_ends_at: toServerIso(discoveryForm.promo_ends_at),
      };
      const r = await fetch(`${BACKEND_URL}/api/admin/discovery/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok) {
        setDiscoveryPricing(data.active_pricing);
        toast.success('Tarification CoachDIA Discovery mise à jour');
      } else {
        toast.error(data?.detail || 'Erreur de sauvegarde Discovery');
      }
    } catch (e) {
      toast.error('Erreur réseau Discovery');
    } finally {
      setSavingDiscovery(false);
    }
  };

  // 🧩 Save COACHDIA Pulse pack (iter234)
  // 🎁 Iter336 — Gratuité premium : promo_price_eur peut être 0 (campagnes
  //   d'accès offert). Si 0 → label + start + end exigés pour traçabilité.
  const handleSaveTeamPack = async (pack) => {
    if (pack.promo_active) {
      const promoNum = pack.promo_price_eur === '' || pack.promo_price_eur === null
        ? null
        : Number(pack.promo_price_eur);
      const baseNum = Number(pack.price_eur);
      if (promoNum !== null) {
        if (Number.isNaN(promoNum) || promoNum < 0 || promoNum > baseNum) {
          toast.error('Promo: prix ≥ 0 et ≤ prix de base requis');
          return;
        }
        if (promoNum === 0) {
          if (!pack.promo_label || !pack.promo_label.trim()) {
            toast.error('Promo gratuite (0 €) : libellé de campagne requis');
            return;
          }
          if (!pack.promo_start) {
            toast.error('Promo gratuite (0 €) : date de début requise');
            return;
          }
          if (!pack.promo_end) {
            toast.error('Promo gratuite (0 €) : date de fin requise');
            return;
          }
        }
      }
    }
    setSavingTeamPackId(pack.pack_id);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: pack.name,
        description: pack.description,
        min_size: Number(pack.min_size),
        max_size: Number(pack.max_size),
        price_eur: Number(pack.price_eur),
        promo_price_eur: (pack.promo_price_eur === '' || pack.promo_price_eur == null)
          ? null
          : Number(pack.promo_price_eur),
        promo_active: !!pack.promo_active,
        promo_label: pack.promo_label || null,
        promo_start: pack.promo_start || null,
        promo_end: pack.promo_end || null,
        active: pack.active !== false,
        sort_order: Number(pack.sort_order || 0),
      };
      const r = await fetch(`${BACKEND_URL}/api/discovery/admin/team-insights/packs/${pack.pack_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (r.ok) {
        setTeamPacks((prev) => prev.map((p) => (p.pack_id === pack.pack_id ? data.pack : p)));
        toast.success(`Pack ${pack.name} mis à jour`);
      } else {
        toast.error(data?.detail || 'Erreur de sauvegarde');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    } finally {
      setSavingTeamPackId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Plans Actifs</p>
                <p className="text-2xl font-bold">{stats.active_prices}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Promotions Actives</p>
                <p className="text-2xl font-bold text-amber-600">{stats.active_coupons}</p>
              </div>
              <Tag className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Utilisations</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_coupon_redemptions}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Promotions Section - NEW */}
      <div className="mb-6">
        <DynamicPromotions />
      </div>

      {/* 🎯 Subscription Packs - Quotas de Sessions */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg shadow-lg border-2 border-emerald-200 mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-emerald-900">🎯 Quotas de Sessions par Pack</h3>
              <p className="text-sm text-gray-600 mt-1">
                Gérez le nombre de sessions incluses dans chaque pack d&apos;abonnement
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow">
          {subscriptionPacks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun pack configuré</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {subscriptionPacks.map((pack) => (
                <div 
                  key={pack.pack_id} 
                  data-testid={`pack-row-${pack.pack_id}`}
                  className={`p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-gray-50 transition-colors ${!pack.is_active ? 'opacity-60 bg-gray-50' : ''}`}
                >
                  {/* Pack Info - Left Side */}
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center ${
                      pack.pack_id === 'pack_decouverte' ? 'bg-blue-100' :
                      pack.pack_id === 'pack_standard' ? 'bg-amber-100' :
                      'bg-purple-100'
                    }`}>
                      <Clock className={`w-6 h-6 ${
                        pack.pack_id === 'pack_decouverte' ? 'text-blue-600' :
                        pack.pack_id === 'pack_standard' ? 'text-amber-600' :
                        'text-purple-600'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900">{pack.name}</h4>
                        {pack.is_popular && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                            Populaire
                          </span>
                        )}
                        {!pack.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                            Inactif
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{pack.price_display}</p>
                    </div>
                  </div>
                  
                  {/* Quota + Edit Button - Right Side */}
                  <div className="flex items-center justify-between sm:justify-end gap-4 ml-16 sm:ml-0">
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <span 
                          data-testid={`pack-quota-${pack.pack_id}`}
                          className="text-2xl sm:text-3xl font-bold text-emerald-600"
                        >
                          {pack.sessions_quota}
                        </span>
                        <span className="text-sm text-gray-500">sessions</span>
                      </div>
                      {pack.updated_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Modifié: {new Date(pack.updated_at).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`edit-quota-${pack.pack_id}`}
                      onClick={() => {
                        setEditingPack(pack);
                        setShowEditQuotaModal(true);
                      }}
                      className="p-3 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors flex-shrink-0 touch-manipulation"
                      title="Modifier le quota"
                      aria-label={`Modifier le quota du pack ${pack.name}`}
                    >
                      <Edit2 className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Info section */}
        <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
          <p className="text-sm text-emerald-800">
            <strong>Source de vérité :</strong> Les quotas sont stockés dans la collection <code className="bg-white px-1 rounded">subscription_packs</code> MongoDB.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Ces valeurs sont utilisées par le système de facturation pour attribuer les sessions lors de l&apos;achat d&apos;un pack.
          </p>
        </div>
      </div>

      {/* ✨ CoachDIA Discovery (B2C COACHDIA Prism) — source unique, UNIFIÉ ICI */}
      <div
        data-testid="pricing-discovery-block"
        className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg shadow-lg border-2 border-amber-200 mb-6 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-900">✨ CoachDIA Discovery (COACHDIA Prism B2C)</h3>
              <p className="text-sm text-gray-600 mt-1">
                Prix, promotion et statut du produit Discovery — source de vérité unique
              </p>
            </div>
          </div>
          {discoveryPricing?.promo_active ? (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-600 text-white" data-testid="discovery-promo-badge-active">
              PROMO ACTIVE
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700" data-testid="discovery-promo-badge-inactive">
              Standard
            </span>
          )}
        </div>

        {/* Live preview */}
        <div className="bg-white rounded-lg p-5 shadow mb-5" data-testid="discovery-active-preview">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest">Prix affiché à l&apos;utilisateur (live)</p>
              <p className="text-4xl font-bold text-amber-700 mt-1">
                {discoveryPricing?.amount_eur?.toFixed(2) ?? discoveryForm.base_price_eur} {discoveryPricing?.currency || discoveryForm.currency}
              </p>
              {discoveryPricing?.promo_active && (
                <p className="text-sm text-amber-700 mt-1">
                  🎁 {discoveryPricing.promo_label || 'Promo'} · -{discoveryPricing.discount_pct?.toFixed(0)}%
                  <span className="text-gray-500 ml-2 line-through">{discoveryPricing.base_price_eur} €</span>
                </p>
              )}
            </div>
            <div className="text-right text-xs text-gray-500 uppercase tracking-widest">
              Éditable ci-dessous
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg p-5 shadow space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prix de base (€) *</label>
              <input
                data-testid="discovery-input-base-price"
                type="number"
                min="1"
                step="1"
                value={discoveryForm.base_price_eur}
                onChange={e => setDiscoveryForm({ ...discoveryForm, base_price_eur: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-xl font-bold text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
              <select
                data-testid="discovery-input-currency"
                value={discoveryForm.currency}
                onChange={e => setDiscoveryForm({ ...discoveryForm, currency: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="CHF">CHF</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">Promotion temporaire</p>
                <p className="text-xs text-gray-500">Activer une réduction sur une période définie</p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  data-testid="discovery-switch-promo"
                  type="checkbox"
                  checked={discoveryForm.promo_enabled}
                  onChange={e => setDiscoveryForm({ ...discoveryForm, promo_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
              </label>
            </div>

            {discoveryForm.promo_enabled && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prix promo (€) *</label>
                    <input
                      data-testid="discovery-input-promo-price"
                      type="number"
                      min="1"
                      step="1"
                      value={discoveryForm.promo_price_eur}
                      onChange={e => setDiscoveryForm({ ...discoveryForm, promo_price_eur: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 font-semibold text-amber-700"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                    <input
                      data-testid="discovery-input-promo-label"
                      type="text"
                      placeholder="Lancement, Black Friday…"
                      value={discoveryForm.promo_label}
                      onChange={e => setDiscoveryForm({ ...discoveryForm, promo_label: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Début (optionnel)</label>
                    <input
                      data-testid="discovery-input-promo-start"
                      type="datetime-local"
                      value={discoveryForm.promo_starts_at}
                      onChange={e => setDiscoveryForm({ ...discoveryForm, promo_starts_at: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Fin (optionnel)</label>
                    <input
                      data-testid="discovery-input-promo-end"
                      type="datetime-local"
                      value={discoveryForm.promo_ends_at}
                      onChange={e => setDiscoveryForm({ ...discoveryForm, promo_ends_at: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            data-testid="discovery-save-btn"
            onClick={handleSaveDiscoveryPricing}
            disabled={savingDiscovery}
            className="w-full md:w-auto px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium shadow-md"
          >
            {savingDiscovery ? (<><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</>) : (<><Save className="w-4 h-4" /> Enregistrer Discovery</>)}
          </button>
        </div>

        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-sm text-amber-900">
            <strong>Source de vérité :</strong> collection MongoDB <code className="bg-white px-1 rounded">discovery_pricing_config</code>.
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Endpoint : <code className="bg-white px-1 rounded">PUT /api/admin/discovery/pricing</code> — mise à jour immédiate sur les nouveaux checkouts.
          </p>
        </div>
      </div>

      {/* 🧩 COACHDIA Pulse packs (iter234) — pricing par taille d'équipe + promos */}
      <div
        data-testid="pricing-team-insights-block"
        className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg shadow-lg border-2 border-indigo-200 mb-6 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-indigo-900">🧩 CoachDIA COACHDIA Pulse (packs B2B)</h3>
              <p className="text-sm text-gray-600 mt-1">
                Pack sélectionné automatiquement selon la taille d'équipe. Promotions par pack.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {teamPacks.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">Aucun pack configuré — seed en cours…</p>
          )}
          {teamPacks.map((p) => {
            const isExpanded = expandedPack === p.pack_id;
            return (
              <div
                key={p.pack_id}
                data-testid={`team-pack-row-${p.pack_id}`}
                className={`bg-white rounded-lg border ${p.active ? 'border-indigo-200' : 'border-gray-300 opacity-60'} overflow-hidden`}
              >
                <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-500">#{p.sort_order}</span>
                      <h4 className="font-semibold text-indigo-900">{p.name}</h4>
                      {!p.active && <span className="text-[10px] px-2 py-0.5 bg-gray-200 rounded-full uppercase">Inactif</span>}
                      {p.promo_effective && <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-white rounded-full uppercase font-semibold">Promo -{p.discount_pct}%</span>}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{p.min_size}-{p.max_size} profils</p>
                  </div>
                  <div className="text-right">
                    {p.promo_effective ? (
                      <>
                        <span className="text-xl font-bold text-amber-700">{p.effective_price_eur}€</span>
                        <span className="ml-2 text-gray-400 line-through text-sm">{p.base_price_eur}€</span>
                      </>
                    ) : (
                      <span className="text-xl font-bold text-indigo-900">{p.base_price_eur === 0 ? 'Devis' : `${p.base_price_eur}€`}</span>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedPack(isExpanded ? null : p.pack_id)}
                    data-testid={`team-pack-edit-${p.pack_id}`}
                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    {isExpanded ? 'Fermer' : 'Éditer'}
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-indigo-100 p-4 bg-indigo-50/30 space-y-3" data-testid={`team-pack-form-${p.pack_id}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Min</label>
                        <input type="number" value={p.min_size}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, min_size: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"/>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max</label>
                        <input type="number" value={p.max_size}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, max_size: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"/>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Prix (€)</label>
                        <input type="number" step="0.01" value={p.price_eur}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, price_eur: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-semibold"/>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Ordre</label>
                        <input type="number" value={p.sort_order}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, sort_order: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"/>
                      </div>
                    </div>
                    <textarea value={p.description || ''}
                      onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, description: e.target.value } : x))}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs" rows={2}/>

                    <div className="border-t border-indigo-100 pt-3">
                      <label className="flex items-center gap-2 mb-2">
                        <input type="checkbox" checked={!!p.promo_active}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, promo_active: e.target.checked } : x))}
                          data-testid={`team-pack-promo-active-${p.pack_id}`}/>
                        <span className="text-sm font-medium">Promotion activée</span>
                      </label>
                      {p.promo_active && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <input type="number" step="0.01" placeholder="Prix promo"
                            value={p.promo_price_eur || ''}
                            onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, promo_price_eur: e.target.value } : x))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"/>
                          <input type="text" placeholder="Label" value={p.promo_label || ''}
                            onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, promo_label: e.target.value } : x))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"/>
                          <input type="datetime-local" value={p.promo_start ? p.promo_start.slice(0, 16) : ''}
                            onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, promo_start: e.target.value ? new Date(e.target.value).toISOString() : null } : x))}
                            className="border border-gray-300 rounded px-2 py-1 text-xs"/>
                          <input type="datetime-local" value={p.promo_end ? p.promo_end.slice(0, 16) : ''}
                            onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, promo_end: e.target.value ? new Date(e.target.value).toISOString() : null } : x))}
                            className="border border-gray-300 rounded px-2 py-1 text-xs"/>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={p.active !== false}
                          onChange={(e) => setTeamPacks(prev => prev.map(x => x.pack_id === p.pack_id ? { ...x, active: e.target.checked } : x))}/>
                        <span className="text-sm">Pack actif</span>
                      </label>
                      <button
                        onClick={() => handleSaveTeamPack(p)}
                        disabled={savingTeamPackId === p.pack_id}
                        data-testid={`team-pack-save-${p.pack_id}`}
                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 text-sm flex items-center gap-2"
                      >
                        {savingTeamPackId === p.pack_id
                          ? (<><Loader2 className="w-3 h-3 animate-spin" /> Enregistrement…</>)
                          : (<><Save className="w-3 h-3" /> Enregistrer</>)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 p-3 bg-indigo-100/50 rounded-lg border border-indigo-200 text-xs text-indigo-900">
          <strong>Source de vérité :</strong> <code className="bg-white px-1 rounded">team_insights_pricing_config</code>. Endpoint : <code className="bg-white px-1 rounded">PUT /api/discovery/admin/team-insights/packs/:id</code>
        </div>
      </div>

      {/* Local Pricing Plan (Used in billing.py) */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-lg border-2 border-blue-200 mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">💶 Prix d'Abonnement Premium</h3>
            <p className="text-sm text-gray-600 mt-1">
              Ce prix est utilisé pour les paiements Stripe (défini dans le backend)
            </p>
          </div>
        </div>
        
        {localPlans.length > 0 ? (
          <div className="bg-white rounded-lg p-6 shadow">
            {localPlans.map((plan) => (
              <div key={plan.id} className="flex items-center justify-between">
                <div>
                  <h4 className="text-2xl font-bold text-blue-600">{plan.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  <div className="mt-3">
                    <span className="text-4xl font-bold text-green-600">{plan.price.toFixed(2)}€</span>
                    <span className="text-gray-500 ml-2">/mois</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {plan.features?.map((feature, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                        ✓ {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingPlan(plan);
                    setShowEditPriceModal(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Modifier le Prix
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-4 text-center text-gray-500">
            Aucun plan configuré (utilisera 22,90€ par défaut)
          </div>
        )}
      </div>

      {/* 🏢 B2B: Enterprise Pricing Section */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow-lg border-2 border-purple-200 mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-purple-900">🏢 Configuration Entreprise (B2B)</h3>
              <p className="text-sm text-gray-600 mt-1">
                Prix unitaire et quota de sessions pour les bénéficiaires entreprise
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-6 shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Prix unitaire */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prix unitaire mensuel (€/licence/mois)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={enterprisePrice}
                  onChange={(e) => setEnterprisePrice(e.target.value)}
                  placeholder="Ex: 25.00"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-2xl font-bold text-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  €/mois
                </span>
              </div>
            </div>
            
            {/* 🔒 P1 MOA: Quota mensuel B2B */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quota sessions mensuel par bénéficiaire
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="1000"
                  step="1"
                  value={enterpriseQuota}
                  onChange={(e) => setEnterpriseQuota(e.target.value)}
                  placeholder="150"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-2xl font-bold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  sessions/mois
                </span>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleUpdateEnterprisePrice}
            disabled={savingEnterprisePrice || !enterprisePrice || !enterpriseQuota}
            className="w-full md:w-auto px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors shadow-md hover:shadow-lg"
          >
            {savingEnterprisePrice ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Enregistrer la configuration
              </>
            )}
          </button>
          
          {/* Info section */}
          <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
            <p className="text-sm text-purple-800">
              <strong>Formule de calcul :</strong> Prix total = N (licences) × D (mois) × {enterprisePrice || '?'}€
            </p>
            <p className="text-sm text-blue-800 mt-2">
              <strong>Quota bénéficiaires :</strong> Chaque bénéficiaire reçoit automatiquement <strong>{enterpriseQuota || '150'} sessions/mois</strong>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Ce paramètre est la <strong>source de vérité unique</strong> pour l&apos;allocation des crédits entreprise.
            </p>
            {enterprisePricing?.updated_at && (
              <p className="text-xs text-gray-500 mt-2">
                Dernière modification : {new Date(enterprisePricing.updated_at).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Plans Stripe */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Plans d'Abonnement</h3>
        </div>
        <div className="p-4">
          {prices.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Aucun plan configuré</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prices.map((price) => (
                <div key={price.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold">{price.nickname || 'Plan sans nom'}</h4>
                      <p className="text-sm text-gray-500">ID: {price.id}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${price.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {price.active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {price.amount_decimal.toFixed(2)} {price.currency.toUpperCase()}
                    <span className="text-sm text-gray-500 font-normal">/{price.interval}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Promotions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Promotions & Coupons</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            <Plus className="w-4 h-4" />
            Créer Promotion
          </button>
        </div>
        <div className="overflow-x-auto">
          {promotions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune promotion créée</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Réduction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {promotions.map((promo) => (
                  <tr key={promo.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm">{promo.id}</div>
                      {promo.name && <div className="text-xs text-gray-500">{promo.name}</div>}
                    </td>
                    <td className="px-6 py-4">
                      {promo.percent_off ? (
                        <span className="font-semibold text-amber-600">{promo.percent_off}%</span>
                      ) : (
                        <span className="font-semibold text-amber-600">{promo.amount_off/100} {promo.currency?.toUpperCase()}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {promo.duration === 'forever' && 'Permanent'}
                      {promo.duration === 'once' && 'Une fois'}
                      {promo.duration === 'repeating' && `${promo.duration_in_months} mois`}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {promo.times_redeemed} {promo.max_redemptions && `/ ${promo.max_redemptions}`}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${promo.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {promo.valid ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDeletePromotion(promo.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={!promo.valid}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreatePromotionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}
      {showEditPriceModal && <EditPriceModal plan={editingPlan} onClose={() => setShowEditPriceModal(false)} onUpdate={handleUpdatePrice} />}
      {showEditQuotaModal && editingPack && (
        <EditQuotaModal 
          pack={editingPack} 
          onClose={() => {
            setShowEditQuotaModal(false);
            setEditingPack(null);
          }} 
          onUpdate={handleUpdatePackQuota} 
        />
      )}
    </div>
  );
};

// Edit Price Modal
const EditPriceModal = ({ plan, onClose, onUpdate }) => {
  const [newPrice, setNewPrice] = useState(plan?.price || 29.0);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPrice <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      await onUpdate(plan.id, newPrice);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Modifier le Prix</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Plan</label>
            <p className="text-lg font-semibold text-blue-600">{plan?.name}</p>
            <p className="text-sm text-gray-500">{plan?.description}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Prix actuel</label>
            <p className="text-2xl font-bold text-gray-700">{plan?.price.toFixed(2)}€ /mois</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Nouveau prix (EUR)</label>
            <div className="relative">
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg pr-12"
                placeholder="29.00"
              />
              <span className="absolute right-3 top-2 text-gray-500">€</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ce prix sera utilisé pour toutes les nouvelles sessions de paiement Stripe
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Quota Modal for Subscription Packs
const EditQuotaModal = ({ pack, onClose, onUpdate }) => {
  const [newQuota, setNewQuota] = useState(pack?.sessions_quota || 30);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newQuota <= 0) {
      toast.error('Le quota doit être supérieur à 0');
      return;
    }

    setLoading(true);
    try {
      await onUpdate(pack.pack_id, newQuota);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Modifier le Quota de Sessions</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Pack</label>
            <p className="text-lg font-semibold text-emerald-600">{pack?.name}</p>
            <p className="text-sm text-gray-500">{pack?.price_display}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Quota actuel</label>
            <p className="text-2xl font-bold text-gray-700">{pack?.sessions_quota} sessions</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Nouveau quota</label>
            <div className="relative">
              <input
                type="number"
                required
                min="1"
                step="1"
                value={newQuota}
                onChange={(e) => setNewQuota(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg pr-20 text-xl font-bold"
                placeholder="30"
                data-testid="quota-input"
              />
              <span className="absolute right-3 top-2 text-gray-500">sessions</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ce quota sera appliqué à tous les nouveaux achats de ce pack.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              disabled={loading}
              data-testid="cancel-quota-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || newQuota === pack?.sessions_quota}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="save-quota-btn"
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Promotion Modal
const CreatePromotionModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'percent',
    percent_off: '',
    amount_off: '',
    duration: 'once',
    duration_in_months: '',
    max_redemptions: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        duration: formData.duration
      };

      if (formData.type === 'percent') {
        payload.percent_off = parseInt(formData.percent_off);
      } else {
        payload.amount_off = parseInt(formData.amount_off) * 100; // Convert to cents
        payload.currency = 'eur';
      }

      if (formData.duration === 'repeating') {
        payload.duration_in_months = parseInt(formData.duration_in_months);
      }

      if (formData.max_redemptions) {
        payload.max_redemptions = parseInt(formData.max_redemptions);
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/promotion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Promotion créée avec succès');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Créer une Promotion</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nom de la promotion</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ex: Offre Lancement"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Type de réduction</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="percent">Pourcentage</option>
              <option value="amount">Montant fixe</option>
            </select>
          </div>

          {formData.type === 'percent' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Pourcentage (%)</label>
              <input
                type="number"
                required
                min="1"
                max="100"
                value={formData.percent_off}
                onChange={(e) => setFormData({...formData, percent_off: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: 20"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Montant (€)</label>
              <input
                type="number"
                required
                min="1"
                value={formData.amount_off}
                onChange={(e) => setFormData({...formData, amount_off: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: 10"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Durée</label>
            <select
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="once">Une seule fois</option>
              <option value="repeating">Récurrent (X mois)</option>
              <option value="forever">Permanent</option>
            </select>
          </div>

          {formData.duration === 'repeating' && (
            <div>
              <label className="block text-sm font-medium mb-1">Nombre de mois</label>
              <input
                type="number"
                required
                min="1"
                value={formData.duration_in_months}
                onChange={(e) => setFormData({...formData, duration_in_months: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ex: 3"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Limite d'utilisations (optionnel)</label>
            <input
              type="number"
              min="1"
              value={formData.max_redemptions}
              onChange={(e) => setFormData({...formData, max_redemptions: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Illimité"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingManagement;
